import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ProviderLayout from '../../components/ProviderLayout';
import { apiFetch } from '../../api';
import { formatDateTime } from '../../lib/datetime';

const PRIMARY = '#4F46E5';

function asArr(d) {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.items)) return d.items;
  return [];
}

function budgetLabel(b) {
  if (b == null) return 'Kelishilgan';
  return `${b} so'm`;
}

export default function ProviderOrdersPage() {
  const { auth } = useAuth();
  const [segment, setSegment] = useState('feed'); // 'feed' | 'active'
  const [feed, setFeed] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load both independently so one failure doesn't blank the screen.
      const [feedRes, ordersRes] = await Promise.allSettled([
        apiFetch('/api/provider/tasks/feed', auth.token),
        apiFetch('/api/provider/orders/active', auth.token),
      ]);
      let anyOk = false;
      if (feedRes.status === 'fulfilled' && feedRes.value.ok) {
        setFeed(asArr(await feedRes.value.json())); anyOk = true;
      }
      if (ordersRes.status === 'fulfilled' && ordersRes.value.ok) {
        setOrders(asArr(await ordersRes.value.json())); anyOk = true;
      }
      if (!anyOk) setError('Buyurtmalarni yuklashda xatolik yuz berdi.');
    } catch {
      setError('Buyurtmalarni yuklashda xatolik yuz berdi.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const list = segment === 'feed' ? feed : orders;

  return (
    <ProviderLayout title="Buyurtmalar">
      <div style={s.segRow}>
        <Seg label="Yangi vazifalar" active={segment === 'feed'} onClick={() => setSegment('feed')} />
        <Seg label="Buyurtmalarim" active={segment === 'active'} onClick={() => setSegment('active')} />
      </div>

      {loading ? (
        <div style={s.center}>Yuklanmoqda...</div>
      ) : error ? (
        <div style={s.error}>{error}</div>
      ) : list.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📦</div>
          <p style={s.emptyText}>
            {segment === 'feed' ? "Hozircha mos vazifa yo'q" : "Aktiv buyurtma yo'q"}
          </p>
          <p style={s.emptyHint}>Yangi vazifalar shu yerda paydo bo'ladi.</p>
        </div>
      ) : (
        list.map((item, i) => (
          <div key={item.id ?? item.taskId ?? i} style={s.card}>
            <div style={s.cardTop}>
              <span style={s.cardTitle}>
                {item.customerName || item.categoryName || `Vazifa #${item.taskId ?? item.id}`}
              </span>
              {item.status && <span style={s.badge}>{item.status}</span>}
            </div>
            {item.description && <p style={s.cardDesc}>{item.description}</p>}
            {item.address && <p style={s.meta}>{`Manzil: ${item.address}`}</p>}
            <p style={s.budget}>{budgetLabel(item.budget ?? item.price ?? item.amount)}</p>
            {formatDateTime(item.createdAt) && <p style={s.date}>{formatDateTime(item.createdAt)}</p>}
          </div>
        ))
      )}
    </ProviderLayout>
  );
}

function Seg({ label, active, onClick }) {
  return (
    <button style={{ ...s.seg, ...(active ? s.segActive : {}) }} onClick={onClick}>
      <span style={{ color: active ? '#fff' : '#64748B', fontSize: '13px', fontWeight: '600' }}>
        {label}
      </span>
    </button>
  );
}

const s = {
  segRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
  seg: {
    flex: 1,
    padding: '10px',
    borderRadius: '12px',
    background: '#F1F5F9',
    border: 'none',
    cursor: 'pointer',
  },
  segActive: { background: PRIMARY },
  center: { textAlign: 'center', padding: '48px', color: 'var(--slate-400)' },
  error: {
    background: 'var(--danger-tint)',
    border: '1px solid #FCA5A5',
    borderRadius: '10px',
    padding: '12px 14px',
    color: 'var(--danger)',
    fontSize: '14px',
  },
  empty: { textAlign: 'center', padding: '48px 16px' },
  emptyIcon: { fontSize: '40px', marginBottom: '10px' },
  emptyText: { margin: '0 0 4px', fontSize: '16px', fontWeight: '600', color: '#334155' },
  emptyHint: { margin: 0, fontSize: '14px', color: 'var(--slate-400)' },
  card: {
    background: 'var(--surface)',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    padding: '16px',
    marginBottom: '12px',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: PRIMARY },
  badge: {
    background: '#EEF2FF',
    color: PRIMARY,
    borderRadius: '12px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  cardDesc: { fontSize: '15px', color: 'var(--slate-900)', margin: '0 0 6px', lineHeight: 1.4 },
  meta: { fontSize: '13px', color: '#64748B', margin: '0 0 4px' },
  budget: { fontSize: '15px', fontWeight: '700', color: 'var(--slate-900)', margin: '4px 0 0' },
  date: { fontSize: '12px', color: 'var(--slate-400)', margin: '4px 0 0' },
};
